import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AdminFileItem, getAdminFiles, uploadAdminFile, deleteAdminFile } from '../services/adminApi';

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let size = bytes / 1024;
  let unit = units[0];

  for (let i = 1; i < units.length; i += 1) {
    if (size < 1024) {
      unit = units[i - 1];
      break;
    }
    size /= 1024;
    unit = units[i];
  }

  return `${size.toFixed(1)} ${unit}`;
};

const getFileCategory = (type: string) => {
  if (type.startsWith('image/')) return 'Image';
  if (type.startsWith('video/')) return 'Video';
  if (type === 'application/pdf') return 'PDF';
  if (type.includes('word') || type.includes('sheet') || type.includes('presentation') || type.includes('text')) return 'Document';
  return 'File';
};

const FilesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [files, setFiles] = useState<AdminFileItem[]>([]);
  const [filterText, setFilterText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const filteredFiles = files.filter((file) => {
    const query = filterText.trim().toLowerCase();
    if (!query) return true;
    return (
      file.fileName.toLowerCase().includes(query) ||
      file.contentType.toLowerCase().includes(query)
    );
  });

  const loadFiles = async () => {
    setError(null);
    try {
      const loaded = await getAdminFiles();
      setFiles(loaded);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('pages.filesLoadFailed', 'Unable to load files.'));
    }
  };

  useEffect(() => {
    loadFiles();
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(event.target.files?.[0] ?? null);
    setSuccess(null);
    setError(null);
  };

  const handleUpload = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedFile) {
      setError(t('pages.filesSelectFile', 'Select a file to upload.'));
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      await uploadAdminFile(selectedFile);
      setSuccess(t('pages.filesUploadSuccess', 'File uploaded successfully.'));
      setSelectedFile(null);
      (event.target as HTMLFormElement).reset();
      await loadFiles();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('pages.filesUploadFailed', 'Upload failed.'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (file: AdminFileItem) => {
    if (!confirm(t('pages.filesDeleteConfirm', 'Delete this file?'))) return;

    setError(null);
    setSuccess(null);

    try {
      await deleteAdminFile(file.id);
      setSuccess(t('pages.filesDeleteSuccess', 'File removed successfully.'));
      await loadFiles();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('pages.filesDeleteFailed', 'Delete failed.'));
    }
  };

  return (
    <div className="page-grid">
      <div className="section-heading">
        <h2>{t('pages.filesTitle', 'Media Library')}</h2>
        <p>{t('pages.filesDescription', 'Upload images, videos, and documents to Firestore storage and view uploaded files.')}</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert" style={{ background: '#ecfdf5', borderColor: '#bbf7d0', color: '#065f46' }}>{success}</div>}

      <div className="admin-panel">
        <h3 style={{ marginTop: 0 }}>{t('pages.filesUploadSection', 'Upload a file')}</h3>
        <form onSubmit={handleUpload} className="form-grid">
          <label className="form-group">
            <span>{t('pages.filesInputLabel', 'Choose image, video or document')}</span>
            <input
              type="file"
              accept="image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain"
              onChange={handleFileChange}
              className="form-control"
            />
          </label>
          <div className="admin-actions" style={{ marginTop: '0.5rem' }}>
            <button type="submit" className="btn btn-primary" disabled={uploading}>
              {uploading ? t('common.loading', 'Loading...') : t('pages.filesUploadButton', 'Upload file')}
            </button>
          </div>
        </form>
      </div>

      <div className="admin-panel">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: '0 0 0.5rem' }}>{t('pages.filesListTitle', 'Uploaded files')}</h3>
            <p className="muted" style={{ margin: 0 }}>{t('pages.filesListDescription', 'View your image, video, and document uploads stored in Firestore.')}</p>
          </div>
        </div>

        <div className="form-group" style={{ marginTop: '1rem', maxWidth: 420 }}>
          <label>
            <span>{t('pages.filesSearchLabel', 'Search files')}</span>
            <input
              type="search"
              value={filterText}
              onChange={(event) => setFilterText(event.target.value)}
              placeholder={t('pages.filesSearchPlaceholder', 'Search file name or type')}
              className="form-control"
            />
          </label>
        </div>

        {files.length === 0 ? (
          <p className="muted" style={{ marginTop: '1rem' }}>{t('pages.filesEmptyMessage', 'No files have been uploaded yet.')}</p>
        ) : (
          <>
            {filteredFiles.length === 0 ? (
              <p className="muted" style={{ marginTop: '1rem' }}>{t('pages.filesSearchNoResults', 'No files match your search.')}</p>
            ) : (
              <div style={{ display: 'grid', gap: '1rem', marginTop: '1rem' }}>
                {filteredFiles.map((file) => (
                  <div
                    key={file.id}
                    className="admin-panel clickable-admin-panel"
                    style={{ padding: '1rem' }}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/admin/files/${file.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(`/admin/files/${file.id}`);
                      }
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: 0 }}>{file.fileName}</h4>
                        <p className="muted" style={{ margin: '0.5rem 0 0' }}>
                          {getFileCategory(file.contentType)} • {formatBytes(file.size)} • {new Date(file.uploadedAt).toLocaleString()}
                        </p>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ marginTop: '0.75rem' }}
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/admin/files/${file.id}`);
                          }}
                        >
                          {t('pages.filesDetailsButton', 'Details')}
                        </button>
                      </div>
                      <div className="admin-actions">
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-secondary"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {t('pages.filesDownloadButton', 'Open')}
                        </a>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDelete(file);
                          }}
                        >
                          {t('common.delete', 'Delete')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FilesPage;
